import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type * as React from 'react';
import { Fragment } from 'react';

interface ModalProperties {
  title: string;
  visible: boolean;
  onClose: () => void;
  size?: 'sm' | 'lg';
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const sizeStyles = {
  sm: { maxWidth: '32rem' },
  lg: { maxWidth: '48rem' },
};

// Note: not using onClose for the dialog, as clicks on notifications will close it.
// https://github.com/tailwindlabs/headlessui/discussions/2301
export const Modal: React.FC<ModalProperties> = ({
  children,
  title,
  onClose,
  visible,
  size = 'lg',
  footer,
}) => {
  return (
    <Transition show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full"
                style={sizeStyles[size]}
              >
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                    {title}
                  </DialogTitle>
                  <button
                    type="button"
                    className="-mr-2 flex h-9 w-9 items-center justify-center rounded-md text-icon hover:bg-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary-600"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="size-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="px-6 py-5">{children}</div>

                {footer != null && (
                  <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                    {footer}
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
